import { useEffect, useRef } from "react";

const FOCUSABLE = [
  "a[href]", "button:not([disabled])", "input:not([disabled])",
  "select:not([disabled])", "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

/**
 * Focus management for a modal surface: move focus in, keep Tab inside,
 * restore focus on close, and close on Escape.
 *
 * Hand-rolled rather than pulled from a library because the app already ships
 * Radix for some dialogs and adding a second focus-trap dependency for the
 * three remaining hand-built ones is worse than fifty lines here.
 *
 * Returns a ref to attach to the dialog element.
 *
 * @param {boolean} active   whether the surface is currently open
 * @param {Function} onClose called on Escape
 */
export default function useFocusTrap(active, onClose) {
  const ref = useRef(null);
  const restoreTo = useRef(null);

  useEffect(() => {
    if (!active) return;

    // Captured on open, not on close: by the time we are closing, focus has
    // usually already moved inside the dialog.
    restoreTo.current = document.activeElement;

    const nodes = () => Array.from(ref.current?.querySelectorAll(FOCUSABLE) || []);
    // Fall back to the container itself (it carries tabIndex={-1}) so focus
    // never stays behind the backdrop in a dialog with no focusable content.
    (nodes()[0] || ref.current)?.focus();

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose?.();
        return;
      }
      if (e.key !== "Tab") return;
      const items = nodes();
      if (!items.length) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      // Without the wrap, Tab walks out of the dialog and into the page behind
      // the backdrop — which the user cannot see.
      if (e.shiftKey && (document.activeElement === first || document.activeElement === ref.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      // Guard: the trigger may have unmounted along with the dialog.
      const target = restoreTo.current;
      if (target && document.contains(target)) target.focus();
    };
  }, [active, onClose]);

  return ref;
}
